import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Book, Loader2, ArrowRight, AlertCircle, Search, BookOpen, File, Upload, Menu, Save } from 'lucide-react';
import UploadDocument from './UploadDocument';
import DocumentList from './DocumentList/DocumentList';
import DisplaySynthesis from './DisplaySynthesis.js';
import ConceptView from './ConceptView/ConceptView';
import DocumentDetail from './DocumentDetail/DocumentDetail';
import SimplePDFViewer from './SimplePDFViewer/SimplePDFViewer';
import OfficeViewer from './OfficeViewer/OfficeViewer';
import { makeAuthenticatedRequest } from '../components/APIService';
import { detectDocumentFormat } from '../utils/documentFormats';

const BackEndUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8080';

// Hook pour détecter la taille de l'écran
const useScreenSize = () => {
  const [screenSize, setScreenSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
    height: typeof window !== 'undefined' ? window.innerHeight : 0,
  });

  useEffect(() => {
    const handleResize = () => {
      setScreenSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener('resize', handleResize);
    handleResize(); // Initial call

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isMobile = screenSize.width < 768;
  const isTablet = screenSize.width >= 768 && screenSize.width < 1024;
  const isDesktop = screenSize.width >= 1024;

  return { isMobile, isTablet, isDesktop, width: screenSize.width, height: screenSize.height };
};

// Composant de message sans résultats - Version Responsive
const NoResultsMessage = ({ searchQuery }) => {
  const { isMobile } = useScreenSize();
  
  return (
    <div className={`text-center ${isMobile ? 'py-8' : 'py-12'} bg-white rounded-lg shadow-sm`}>
      <Search className={`${isMobile ? 'h-10 w-10' : 'h-12 w-12'} text-gray-400 mx-auto mb-4`} />
      <h3 className={`${isMobile ? 'text-base' : 'text-lg'} font-medium text-gray-900 mb-2`}>
        Aucun résultat trouvé
      </h3>
      <p className={`text-gray-600 ${isMobile ? 'text-sm px-4' : ''}`}>
        Aucun document ne correspond à votre recherche
        {searchQuery && <span> "{searchQuery}"</span>}
      </p>
      <p className={`text-gray-500 ${isMobile ? 'text-xs' : 'text-sm'} mt-2`}>
        Essayez avec d'autres termes ou vérifiez l'orthographe
      </p>
    </div>
  );
};

// Composant Tabs Mobile - Navigation par swipe ou menu déroulant
const MobileTabs = ({ activeTab, onTabChange, isMobile }) => {
  const [showMenu, setShowMenu] = useState(false);
  
  const tabs = [
    { id: 'documents', label: 'Documents', icon: File },
    { id: 'concepts', label: 'Concepts', icon: BookOpen },
    { id: 'upload', label: 'Ajouter', icon: Upload }
  ];

  const activeTabData = tabs.find(tab => tab.id === activeTab);

  if (!isMobile) {
    return (
      <div className="flex gap-2">
        {tabs.map((tab) => {
          const IconComponent = tab.icon;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => onTabChange(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                activeTab === tab.id 
                ? 'bg-blue-100 text-blue-700 font-medium' 
                : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
              }`}
            >
              <IconComponent className="h-4 w-4" />
              {tab.label}
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setShowMenu(!showMenu)}
        className="flex items-center justify-between w-full px-4 py-2 bg-blue-100 text-blue-700 rounded-lg font-medium"
      >
        <div className="flex items-center gap-2">
          <activeTabData.icon className="h-4 w-4" />
          <span>{activeTabData.label}</span>
        </div>
        <Menu className={`h-4 w-4 transition-transform ${showMenu ? 'rotate-180' : ''}`} />
      </button>
      
      {showMenu && (
        <>
          <div 
            className="fixed inset-0 z-10" 
            onClick={() => setShowMenu(false)}
          />
          <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20">
            {tabs.map((tab) => {
              const IconComponent = tab.icon;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => {
                    onTabChange(tab.id);
                    setShowMenu(false);
                  }}
                  className={`flex items-center gap-2 w-full px-4 py-3 text-left transition-colors first:rounded-t-lg last:rounded-b-lg ${
                    activeTab === tab.id 
                    ? 'bg-blue-50 text-blue-700 font-medium' 
                    : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <IconComponent className="h-4 w-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

const KnowledgeBase = ({ 
  userId, 
  competence, 
  initialViewMode = 'documents',
  onActivity = null,
  showHeader = true,
  embedded = false,
  initialQuery = ''
}) => {
  const { isMobile, isTablet, isDesktop } = useScreenSize();
  
  // États principaux
  const [prompt, setPrompt] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [processingDocuments, setProcessingDocuments] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [synthesis, setSynthesis] = useState(null);
  const [error, setError] = useState(null);
  const [authToken, setAuthToken] = useState(null);
  const [lastTokenRefresh, setLastTokenRefresh] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  
  // États pour les différentes vues
  const [viewMode, setViewMode] = useState(initialViewMode);
  const [selectedDocumentId, setSelectedDocumentId] = useState(null);
  const [selectedConcept, setSelectedConcept] = useState(null);
  const [selectedPassage, setSelectedPassage] = useState(null);
  
  // État pour suivre les suppressions en cours
  const [deletingDocIds, setDeletingDocIds] = useState(new Set());
  
  // Référence pour déclencher l'upload de fichier
  const fileInputRef = React.useRef(null);

  // Observer les changements de initialViewMode
  useEffect(() => {
    if (initialViewMode) {
      setViewMode(initialViewMode);
    }
  }, [initialViewMode]);



  // Fonction pour notifier les activités au module parent
  const notifyActivity = useCallback((activityType, details = {}) => {
    if (onActivity && typeof onActivity === 'function') {
      onActivity({
        type: activityType,
        ...details,
        timestamp: new Date().toISOString()
      });
    }
  }, [onActivity]);

  // Gestion des erreurs
  const handleError = useCallback((error, context = '') => {
    const errorMessage = `${context}: ${error?.message || 'Une erreur est survenue'}`;
    console.error(errorMessage, error);
    setError(errorMessage);
    setTimeout(() => setError(null), 5000);
  }, []);

  // Récupération de la liste des documents
  const fetchDocumentList = useCallback(async () => {
    if (!userId) return;

    try {
      setIsLoading(true);
      const response = await makeAuthenticatedRequest(
        `${BackEndUrl}/api/getUserDocuments${competence?.id ? `?objectiveId=${competence.id}` : ''}`,
        { method: 'GET' }
      );

      const data = await response.json();
      console.log('Réponse liste de documents:', data);
      
      if (data.success && data.documents) {
        setDocuments(data.documents);
        setHasMore(data.pagination?.hasMore || false);
        setCurrentPage(data.pagination?.currentPage || 0);
        
        notifyActivity('documents_loaded', { 
          count: data.documents.length,
          competenceId: competence?.id 
        });
      }
    } catch (err) {
      handleError(err, 'Chargement des documents');
      setDocuments([]);
    } finally {
      setIsLoading(false);
    }
  }, [userId, competence?.id, handleError, notifyActivity]);

  // Fonction searchDocuments mise à jour avec notification d'activité
  const searchDocuments = useCallback(async (searchPrompt, page = 0) => {
    if (!userId || !searchPrompt?.trim()) return;
    
    if (!competence?.id) {
      handleError(new Error('Veuillez sélectionner une compétence pour effectuer une recherche'), 'Compétence requise');
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setProcessingDocuments(false);
      
      const competenceId = competence.id;
      console.log(`Recherche avec competenceId: ${competenceId}`);

      const requestData = {
        query: searchPrompt.trim(),
        userId,
        objectiveId: competenceId,
        options: {
          page,
          limit: 10,
          threshold: 0.4,
          includeSynthesis: true,
          filters: {
            competenceId: competenceId,
            userId
          }
        }
      };

      console.log('Requête /api/searchDocuments:', requestData);

      const response = await makeAuthenticatedRequest(
        `${BackEndUrl}/api/searchDocuments`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(requestData)
        }
      );

      const data = await response.json();
      console.log('Réponse API de recherche:', data);

      if (!data.success) {
        handleError(new Error('Réponse non valide du serveur'));
        setIsLoading(false);
        return;
      }

      if (data.synthesis) {
        console.log('Structure de la synthèse reçue:', {
          hasSummary: !!data.synthesis.summary,
          hasMainConcepts: Array.isArray(data.synthesis.mainConcepts),
          mainConceptsCount: Array.isArray(data.synthesis.mainConcepts) ? data.synthesis.mainConcepts.length : 0,
          mainConceptKeys: Array.isArray(data.synthesis.mainConcepts) && data.synthesis.mainConcepts.length > 0 
            ? Object.keys(data.synthesis.mainConcepts[0]) : []
        });
        
        setSynthesis(data.synthesis);
      } else {
        setSynthesis(null);
      }

      if (Array.isArray(data.documents) && data.documents.length > 0) {
        console.log('Traitement de', data.documents.length, 'documents');
        
        setProcessingDocuments(true);
        
        setTimeout(() => {
          try {
            const processedDocuments = data.documents.map(doc => ({
              id: doc.id,
              title: doc.analysis?.metadata?.title || doc.title || 'Document sans titre',
              fileName: doc.fileName || '',
              fileUrl: doc.fileUrl || '',
              score: doc.score || 0.5,
              metadata: {
                ...doc.metadata || {},
                source: doc.analysis?.metadata?.source || 'Non spécifié'
              },
              analysis: {
                summary: doc.analysis?.summary || 'Pas de résumé disponible',
                concepts: Array.isArray(doc.analysis?.concepts) ? doc.analysis.concepts : [],
                keywords: Array.isArray(doc.analysis?.keywords) ? doc.analysis.keywords : [],
                metadata: doc.analysis?.metadata || {}
              },
              source: doc.analysis?.metadata?.source || 'Non spécifié',
              matchingPassages: Array.isArray(doc.matchingPassages) ? doc.matchingPassages : []
            }));
            
            const relevantDocuments = processedDocuments.filter(doc => 
              doc.score >= requestData.options.threshold
            );
            
            console.log(`Documents pertinents: ${relevantDocuments.length}`);
            
            if (page === 0) {
              setDocuments(relevantDocuments);
            } else {
              setDocuments(prev => {
                const prevIds = new Set(prev.map(d => d.id));
                const newDocs = relevantDocuments.filter(d => !prevIds.has(d.id));
                return [...prev, ...newDocs];
              });
            }
            
            setHasMore(data.pagination?.hasMore || false);
            setCurrentPage(page);
            
            notifyActivity('document_searched', {
              query: searchPrompt.trim(),
              resultsCount: relevantDocuments.length,
              competenceId: competence.id
            });
            
          } finally {
            setProcessingDocuments(false);
            setIsLoading(false);
          }
        }, 100);
      } else {
        if (page === 0) {
          setDocuments([]);
          if (!data.synthesis) {
            setSynthesis(null);
          }
        }
        setIsLoading(false);
      }
    } catch (err) {
      console.error('Erreur lors de la recherche:', err);
      handleError(err, 'Recherche de documents');
      if (page === 0) {
        setDocuments([]);
        setSynthesis(null);
      }
      setIsLoading(false);
      setProcessingDocuments(false);
    }
  }, [userId, competence, handleError, notifyActivity]);

  // ✨ NOUVEAU: permettre un déclenchement externe de la recherche (inline chat)
  const lastSearchedRef = useRef('');
  useEffect(() => {
    const shouldTrigger = embedded && initialQuery && initialQuery.trim() && initialQuery !== lastSearchedRef.current;
    if (shouldTrigger && userId && competence?.id) {
      setPrompt(initialQuery);
      setCurrentPage(0);
      setSynthesis(null);
      setViewMode('documents');
      searchDocuments(initialQuery.trim(), 0);
      lastSearchedRef.current = initialQuery;
    }
  }, [initialQuery, embedded, userId, competence?.id, searchDocuments]);

  // Chargement initial des documents
  useEffect(() => {
    if (userId) {
      fetchDocumentList();
    }
  }, [userId, fetchDocumentList]);

  // Suppression d'un document avec notification
  const deleteDocument = useCallback(async (documentId) => {
    if (deletingDocIds.has(documentId)) {
      console.log(`Document ${documentId} déjà en cours de suppression`);
      return;
    }

    try {
      setDeletingDocIds(prev => {
        const newSet = new Set(prev);
        newSet.add(documentId);
        return newSet;
      });
      
      setDocuments(docs => docs.filter(doc => doc.id !== documentId));
      
      await makeAuthenticatedRequest(
        `${BackEndUrl}/api/deleteDocument/${documentId}`,
        { method: 'DELETE' }
      );
      
      console.log(`Document ${documentId} supprimé avec succès`);
      
      notifyActivity('document_deleted', {
        documentId,
        competenceId: competence?.id
      });
      
    } catch (err) {
      handleError(err, 'Suppression du document');
      fetchDocumentList();
    } finally {
      setDeletingDocIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(documentId);
        return newSet;
      });
    }
  }, [handleError, fetchDocumentList, deletingDocIds, notifyActivity, competence?.id]);

  // Chargement de plus de documents
  const loadMore = useCallback(() => {
    if (!isLoading && !processingDocuments && hasMore) {
      if (prompt.trim()) {
        searchDocuments(prompt, currentPage + 1);
      }
    }
  }, [isLoading, processingDocuments, hasMore, prompt, currentPage, searchDocuments]);

  // Soumission du prompt
  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (!prompt.trim()) return;
    setCurrentPage(0);
    setSynthesis(null);
    await searchDocuments(prompt.trim(), 0);
  }, [prompt, searchDocuments]);

  // ✅ CORRECTION: Fonction pour sauvegarder la recherche actuelle comme note avec TOUS les documents
  const saveCurrentSearchAsNote = useCallback(() => {
    if (!prompt.trim() || documents.length === 0) {
      console.warn('⚠️ Impossible de sauvegarder: pas de recherche ou pas de documents');
      return;
    }
    
    console.log('💾 Démarrage de la sauvegarde de la recherche');
    console.log('📝 Prompt:', prompt.trim());
    console.log('📄 Nombre de documents:', documents.length);
    console.log('🔍 Documents complets:', documents);
    
    // Préparer les documents avec toutes les informations nécessaires
    const documentsToSave = documents.map(doc => {
      console.log('📄 Traitement du document:', {
        id: doc.id,
        title: doc.title,
        fileName: doc.fileName,
        fileUrl: doc.fileUrl,
        hasFileUrl: !!doc.fileUrl
      });
      
      return {
        id: doc.id || doc._id || doc.documentId,
        title: doc.title || doc.fileName || doc.name || 'Document sans titre',
        fileName: doc.fileName || doc.title || doc.name,
        fileUrl: doc.fileUrl || doc.url || doc.downloadURL || doc.fileURL || doc.filePath,
        score: doc.score || doc.similarity || doc.relevance || 0,
        excerpt: doc.matchingPassages && doc.matchingPassages.length > 0
          ? doc.matchingPassages[0].content?.substring(0, 300)
          : doc.analysis?.summary?.substring(0, 300) || '',
        pageNumber: doc.matchingPassages && doc.matchingPassages.length > 0
          ? doc.matchingPassages[0].pageNumber
          : null,
        content: doc.matchingPassages && doc.matchingPassages.length > 0
          ? doc.matchingPassages[0].content
          : doc.analysis?.summary || ''
      };
    });
    
    console.log('✅ Documents préparés pour sauvegarde:', documentsToSave);
    console.log('🔗 Vérification des URLs:', documentsToSave.map(d => ({
      title: d.title,
      hasUrl: !!d.fileUrl,
      url: d.fileUrl
    })));
    
    // Notifier le parent pour sauvegarder la recherche AVEC les documents
    notifyActivity('save_search_as_note', {
      query: prompt.trim(),
      resultsCount: documents.length,
      synthesis: synthesis,
      documents: documentsToSave, // ✅ AJOUT CRITIQUE: Envoyer les documents
      timestamp: new Date().toISOString()
    });
    
    console.log('✅ Notification envoyée au parent avec', documentsToSave.length, 'documents');
    
    // Afficher un message de confirmation
    setError('✅ Recherche sauvegardée dans vos notes !');
    setTimeout(() => setError(null), 3000);
  }, [prompt, documents, synthesis, notifyActivity]);

  // Fonction pour déclencher le sélecteur de fichiers
  const triggerFileUpload = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, []);
  
  // Gestion de l'upload complété avec notification
  const handleUploadComplete = useCallback((doc) => {
    fetchDocumentList();
    
    // En mode non-embedded, revenir à l'onglet documents
    if (!embedded) {
      setViewMode('documents');
    }
    
    notifyActivity('document_uploaded', {
      documentName: doc?.fileName || doc?.title || 'Document',
      documentId: doc?.id,
      competenceId: competence?.id
    });
  }, [fetchDocumentList, embedded, notifyActivity, competence?.id]);

  // Gestion du détail d'un document
  const handleViewDetails = useCallback((documentId) => {
    setSelectedDocumentId(documentId);
  }, []);

  // Fermeture des détails
  const closeDetails = useCallback(() => {
    setSelectedDocumentId(null);
    setSelectedPassage(null);
  }, []);

  // Gestion de l'exploration d'un concept avec notification
  const handleSelectConcept = useCallback((concept) => {
    console.log("Exploration du concept:", concept);
    
    const conceptData = (() => {
      if (typeof concept === 'string') {
        return { concept: concept };
      }
      
      if (concept && typeof concept === 'object') {
        return {
          ...concept,
          concept: concept.concept || 'Concept sans nom'
        };
      }
      
      return { concept: 'Concept inconnu' };
    })();
    
    console.log("Concept normalisé:", conceptData);
    setSelectedConcept(conceptData);
    
    notifyActivity('concept_explored', {
      conceptName: conceptData.concept,
      competenceId: competence?.id
    });
  }, [notifyActivity, competence?.id]);
  
  // Fermeture de l'explorateur de concepts
  const closeConceptView = useCallback(() => {
    setSelectedConcept(null);
  }, []);

  // Gestion du visualiseur PDF
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
  const [selectedPdfDocument, setSelectedPdfDocument] = useState(null);
  const [pdfInitialPage, setPdfInitialPage] = useState(0);

  // Gestion du visualiseur Office
  const [officeViewerOpen, setOfficeViewerOpen] = useState(false);
  const [selectedOfficeDocument, setSelectedOfficeDocument] = useState(null);

  const handleViewDocumentPassage = useCallback((documentId, passage) => {
    if (!documentId) {
      handleError(new Error("ID de document manquant"), "Affichage du passage");
      return;
    }

    const fullDocument = documents.find(doc => doc.id === documentId);
    if (!fullDocument) {
      handleError(new Error(`Document non trouvé (ID: ${documentId})`), "Affichage du passage");
      return;
    }

    try {
      if (!fullDocument.fileUrl) {
        throw new Error("L'URL du document est manquante. Impossible d'ouvrir le document.");
      }

      // Detect format using smart detection
      const formatInfo = detectDocumentFormat(fullDocument.metadata, fullDocument.fileName);
      const mimeType = formatInfo.mimeType;
      const isPDF = mimeType === 'application/pdf';
      const isImage = mimeType?.startsWith('image/');
      const isOffice = mimeType?.includes('officedocument');

      // Non-PDF documents: open according to their format
      if (!isPDF) {
        if (isImage) {
          window.open(fullDocument.fileUrl, '_blank');
        } else if (isOffice) {
          setSelectedOfficeDocument(fullDocument);
          setOfficeViewerOpen(true);
        } else {
          window.open(fullDocument.fileUrl, '_blank');
        }
        return;
      }

      // PDF: extract target page from the clicked passage (0-based index)
      const targetPage = passage?.pageNumber ? parseInt(passage.pageNumber) - 1 : 0;

      setSelectedPdfDocument(fullDocument);
      setPdfInitialPage(targetPage);
      setPdfViewerOpen(true);

    } catch (error) {
      console.error("Erreur lors de la préparation du visualiseur:", error);
      handleError(error, "Préparation de l'affichage du document");
    }
  }, [documents, handleError]);

  const closePDFViewer = useCallback(() => {
    setPdfViewerOpen(false);
    setSelectedPdfDocument(null);
    setPdfInitialPage(0);
  }, []);

  const closeOfficeViewer = useCallback(() => {
    setOfficeViewerOpen(false);
    setSelectedOfficeDocument(null);
  }, []);

  // Classes CSS conditionnelles pour le mode intégré et responsive
  const containerClass = embedded 
    ? "bg-transparent" 
    : `min-h-screen bg-gradient-to-b from-gray-50 to-white ${isMobile ? 'px-2' : ''}`;
  
  const wrapperClass = embedded 
    ? "px-0" 
    : `${isDesktop ? 'max-w-7xl' : 'max-w-full'} mx-auto ${isMobile ? 'px-2' : 'px-4'}`;

  // Déterminer le mode d'affichage effectif
  const effectiveViewMode = viewMode;

  return (
    <div className={containerClass}>
      <div className={wrapperClass}>
        {/* En-tête et zone de recherche - conditionnellement affichée */}
        {showHeader && (
          <div className={`${isMobile ? 'py-4' : 'py-8'}`}>
            <div className={`${isDesktop ? 'max-w-3xl' : 'max-w-full'} mx-auto text-center ${isMobile ? 'mb-4' : 'mb-6'} ${isMobile ? 'px-4' : ''}`}>
              <h2 className={`${isMobile ? 'text-lg' : 'text-xl'} font-semibold text-gray-900 mb-2`}>
                Votre base de connaissances
              </h2>
              <p className={`text-gray-600 ${isMobile ? 'text-sm' : ''}`}>
                Constituez votre base de connaissances personnalisée à partir de sources diversifiées et pertinentes 
              </p>
            </div>
          </div>
        )}

        <div className={showHeader ? (isDesktop ? "max-w-7xl mx-auto" : "max-w-full mx-auto") : ""}>
          <div className={`flex flex-col ${isMobile ? 'space-y-2' : 'space-y-3'} ${isMobile ? 'px-2' : ''}`}>
            {/* Onglets de navigation - masqués en mode embedded */}
            {!embedded && (
              <MobileTabs 
                activeTab={viewMode}
                onTabChange={setViewMode}
                isMobile={isMobile}
              />
            )}

            {/* Zone principale */}
            {effectiveViewMode === 'upload' ? (
              <div className={`p-4 bg-white rounded-lg shadow-sm ${isMobile ? '' : ''}`}>
                <UploadDocument
                  ref={fileInputRef}
                  competence={competence}
                  userId={userId}
                  onUploadComplete={handleUploadComplete}
                />
              </div>
            ) : effectiveViewMode === 'concepts' ? (
              <div className="bg-white rounded-lg shadow-sm">
                <ConceptView
                  userId={userId} 
                  competence={competence}
                  makeAuthenticatedRequest={makeAuthenticatedRequest}
                  onError={handleError}
                />
              </div>
            ) : (
              <div className="space-y-3">
                <form onSubmit={handleSubmit} className="w-full">
                  <div className={`flex items-stretch gap-2 ${isMobile ? 'p-3' : 'p-4'} bg-white rounded-lg shadow-sm`}>
                    <div className="flex-1 relative">
                      <div className={`absolute ${isMobile ? 'left-3' : 'left-4'} top-1/2 -translate-y-1/2 text-gray-400`}>
                        <Search className={`${isMobile ? 'h-4 w-4' : 'h-5 w-5'}`} />
                      </div>
                      <input
                        type="text"
                        className={`w-full ${isMobile ? 'h-10 pl-10 pr-10 text-sm' : 'h-12 pl-12 pr-12 text-base'} border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                        placeholder={isMobile ? "Posez votre question..." : "Posez votre question ici..."}
                        value={prompt}
                        onChange={(e) => setPrompt(e.target.value)}
                        disabled={isLoading || processingDocuments}
                      />
                      <button
                        type="submit"
                        className={`absolute ${isMobile ? 'right-2' : 'right-3'} top-1/2 -translate-y-1/2 p-2 text-blue-600 hover:text-blue-700 disabled:text-gray-400`}
                        disabled={isLoading || processingDocuments || !prompt.trim()}
                      >
                        {isLoading || processingDocuments ? (
                          <Loader2 className={`${isMobile ? 'h-4 w-4' : 'h-5 w-5'} animate-spin`} />
                        ) : (
                          <ArrowRight className={`${isMobile ? 'h-4 w-4' : 'h-5 w-5'}`} />
                        )}
                      </button>
                    </div>
                    
                    {/* Bouton Ajouter - sur la même ligne que la recherche */}
                    {embedded && effectiveViewMode === 'documents' && (
                      <button
                        type="button"
                        onClick={() => setViewMode('upload')}
                        className={`flex items-center gap-1.5 ${isMobile ? 'px-3 py-2' : 'px-4 py-2.5'} bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors ${isMobile ? 'text-xs' : 'text-sm'} font-medium whitespace-nowrap`}
                        title="Ajouter un document"
                      >
                        <Upload className={`${isMobile ? 'h-3.5 w-3.5' : 'h-4 w-4'}`} />
                        <span>Ajouter</span>
                      </button>
                    )}
                  </div>
                </form>
              </div>
            )}
          </div>
        </div>

        {/* Zone de résultats - visible uniquement en mode documents */}
        {effectiveViewMode === 'documents' && (
          <div className={showHeader ? (isDesktop ? "max-w-5xl mx-auto" : "max-w-full mx-auto") : ""}>
            {/* Synthèse des résultats */}
            {synthesis && !isLoading && !processingDocuments && (
              <div className={isMobile ? 'px-2' : ''}>
                <DisplaySynthesis 
                  synthesis={synthesis} 
                  searchQuery={prompt} 
                  documents={documents} 
                  onViewDocumentPassage={handleViewDocumentPassage}
                  onSelectConcept={handleSelectConcept}
                />
                
                {/* Bouton discret pour sauvegarder la recherche - Affiché uniquement après les résultats */}
                {documents.length > 0 && (
                  <div className="mt-3 flex justify-end">
                    <button
                      onClick={saveCurrentSearchAsNote}
                      className="group flex items-center gap-2 px-3 py-1.5 text-gray-600 hover:text-green-700 hover:bg-green-50 border border-gray-200 hover:border-green-300 rounded-lg transition-all text-sm"
                      title="Sauvegarder cette recherche dans mes notes"
                    >
                      <Save className="h-4 w-4" />
                      <span className="text-xs">Sauvegarder cette recherche</span>
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Liste des documents ou états de chargement */}
            {isLoading || processingDocuments ? (
              <div className={`text-center ${isMobile ? 'py-8' : 'py-12'} ${isMobile ? 'px-4' : ''}`}>
                <Loader2 className={`${isMobile ? 'h-10 w-10' : 'h-12 w-12'} text-blue-400 mx-auto mb-4 animate-spin`} />
                <h3 className={`${isMobile ? 'text-base' : 'text-lg'} font-medium text-gray-900 mb-2`}>
                  {processingDocuments ? "Traitement des résultats..." : "Recherche en cours..."}
                </h3>
                <p className={`text-gray-600 ${isMobile ? 'text-sm' : ''}`}>
                  {processingDocuments ? 
                    "Nous analysons les documents et extrayons les concepts pertinents" : 
                    "Nous recherchons les documents correspondant à votre requête"}
                </p>
              </div>
            ) : documents.length === 0 && prompt ? (
              <div className={isMobile ? 'px-2' : ''}>
                <NoResultsMessage searchQuery={prompt} />
              </div>
            ) : documents.length === 0 ? (
              <div className={`text-center ${isMobile ? 'py-8' : 'py-12'} ${isMobile ? 'px-4' : ''}`}>
                <Book className={`${isMobile ? 'h-10 w-10' : 'h-12 w-12'} text-gray-400 mx-auto mb-4`} />
                <h3 className={`${isMobile ? 'text-base' : 'text-lg'} font-medium text-gray-900 mb-2`}>
                  Commencez votre exploration
                </h3>
                <p className={`text-gray-600 ${isMobile ? 'text-sm' : ''}`}>
                  Posez une question ou téléchargez un document pour découvrir les concepts clés
                </p>
              </div>
            ) : (
              <div className={isMobile ? 'px-2' : ''}>
                <DocumentList
                  documents={documents}
                  loading={false}
                  onDelete={deleteDocument}
                  onViewDetails={handleViewDetails}
                  onSelectConcept={handleSelectConcept}
                  onPassageClick={handleViewDocumentPassage}
                  onLoadMore={loadMore}
                  hasMore={hasMore}
                  deletingDocIds={deletingDocIds}
                />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modals - Responsive */}
      {selectedDocumentId && (
        <div className={`fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50 ${isMobile ? 'p-2' : 'p-4'}`}>
          <div className={`w-full ${isMobile ? 'h-full' : 'max-h-[90vh]'} ${isMobile ? '' : 'max-w-4xl'} overflow-auto`}>
            <DocumentDetail 
              documentId={selectedDocumentId} 
              userId={userId}
              competenceId={competence?.id}
              selectedPassage={selectedPassage}
              onClose={closeDetails}
              makeAuthenticatedRequest={makeAuthenticatedRequest}
              onError={handleError}
            />
          </div>
        </div>
      )}
      
      {selectedConcept && (
        <div className={`fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50 ${isMobile ? 'p-2' : 'p-4'}`}>
          <div className={`w-full ${isMobile ? 'h-full' : 'max-w-4xl max-h-[90vh]'} overflow-auto bg-white rounded-lg shadow-xl`}>
            <ConceptView 
              userId={userId}
              competence={competence}
              selectedConcept={selectedConcept}
              onClose={closeConceptView}
              makeAuthenticatedRequest={makeAuthenticatedRequest}
              onError={handleError}
            />
          </div>
        </div>
      )}

      {/* SimplePDFViewer - Lightweight PDF viewer */}
      {pdfViewerOpen && selectedPdfDocument && (
        <SimplePDFViewer
          documentUrl={selectedPdfDocument.fileUrl}
          initialPage={pdfInitialPage}
          onClose={closePDFViewer}
        />
      )}

      {/* OfficeViewer - For Word, Excel, PowerPoint documents */}
      {officeViewerOpen && selectedOfficeDocument && (
        <OfficeViewer
          documentUrl={selectedOfficeDocument.fileUrl}
          fileName={selectedOfficeDocument.fileName || selectedOfficeDocument.title}
          documentType={selectedOfficeDocument.metadata?.mimeType?.includes('word') ? 'Word' :
                       selectedOfficeDocument.metadata?.mimeType?.includes('spreadsheet') ? 'Excel' :
                       selectedOfficeDocument.metadata?.mimeType?.includes('presentation') ? 'PowerPoint' :
                       'Document Office'}
          onClose={closeOfficeViewer}
        />
      )}

      {/* Message d'erreur - Position responsive */}
      {error && (
        <div className={`fixed ${isMobile ? 'bottom-2 left-2 right-2' : 'bottom-4 right-4 max-w-sm'} z-40`}>
          <div className={`${error.includes('✅') ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'} border rounded-lg ${isMobile ? 'p-3' : 'p-4'} flex items-center gap-2`}>
            <AlertCircle className={`${isMobile ? 'h-4 w-4' : 'h-5 w-5'} ${error.includes('✅') ? 'text-green-500' : 'text-red-500'} flex-shrink-0`} />
            <p className={`${error.includes('✅') ? 'text-green-700' : 'text-red-700'} ${isMobile ? 'text-sm' : ''}`}>{error}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default KnowledgeBase;
